import { DEFAULT_PATTERN_ID, DEFAULT_THEME_ID, patternById, themeById } from "../shared/themes";
import { cleanLook, safeThemeId } from "../shared/look_prefs";
import { runAmbient } from "./ambient";
import { applyLook, currentLook, readSavedLook, resolveThemeId } from "./appearance";
import { cachedSettings } from "./settings_cache";

export function paintTheme(themeId: string): void {
  const theme = themeById(themeId);
  document.documentElement.dataset.theme = theme.id;
  document.documentElement.dataset.tone = theme.tone;
  // the person's own colours sit on top of the theme, so they follow whichever theme is on
  applyLook(currentLook());
  try {
    localStorage.setItem("chix-theme", theme.id);
  } catch {
    // private mode
  }
}

export function paintPattern(patternId: string): void {
  const pattern = patternById(patternId);
  document.documentElement.dataset.pattern = pattern.id;
  runAmbient(pattern.id);
  try {
    localStorage.setItem("chix-pattern", pattern.id);
  } catch {
    // private mode
  }
}

// paint the last known theme before the server answers so the first frame is right
export function paintSavedTheme(): void {
  try {
    const saved = localStorage.getItem("chix-theme");
    const pattern = localStorage.getItem("chix-pattern");
    if (pattern) document.documentElement.dataset.pattern = patternById(pattern).id;
    if (saved) {
      // a mature theme left over from when the switch was on does not paint once it is off
      const theme = themeById(safeThemeId(saved, readSavedLook().mature));
      document.documentElement.dataset.theme = theme.id;
      document.documentElement.dataset.tone = theme.tone;
    }
    applyLook(readSavedLook());
  } catch {
    // private mode
  }
}

let followingDevice = false;
let chosenThemeId = DEFAULT_THEME_ID;

// the theme the person picked, kept so following the device can come back to it
export function rememberChosenTheme(themeId: string): void {
  chosenThemeId = themeId;
}

// what to paint right now: their pick, or the day / night one when they follow the device
export function activeThemeId(): string {
  return resolveThemeId(chosenThemeId, currentLook());
}

// the server holds each person's own theme, so whoever logs in on this device gets theirs
export async function bootTheme(): Promise<void> {
  try {
    const settings = await cachedSettings();
    const look = cleanLook(settings.look);
    applyLook(look);
    chosenThemeId = safeThemeId(settings.themeId || DEFAULT_THEME_ID, look.mature);
    paintTheme(activeThemeId());
    paintPattern(settings.patternId || DEFAULT_PATTERN_ID);
  } catch {
    paintTheme(DEFAULT_THEME_ID);
    paintPattern(DEFAULT_PATTERN_ID);
  }
  if (!followingDevice) {
    followingDevice = true;
    matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      if (currentLook().autoOn) paintTheme(activeThemeId());
    });
  }
}
